/**
 * Shared pending-queue rules for admin Demandes and notification deep links.
 * Notifications are created when a document status becomes pending, even if
 * the parent verification request is missing, approved, or rejected.
 */

export const PENDING_REVIEW_STATUSES = ["pending", "en_attente", "under_review"] as const;

export type PendingReviewStatus = (typeof PENDING_REVIEW_STATUSES)[number];

export type DemandeKind = "documents" | "vehicles";

export type DemandeKindFilter = "all" | DemandeKind;

const VEHICLE_DOCUMENT_TYPES = new Set([
  "vehicle_registration",
  "insurance",
  "technical_inspection",
  "business_premises_photo",
  "trade_register",
]);

export const isPendingReviewStatus = (status: string | null | undefined): boolean => {
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  return (
    normalized === "pending" || normalized === "en_attente" || normalized === "under_review"
  );
};

export const classifyDemandeKind = (
  verificationType: string | null | undefined,
  documentTypes: string[]
): DemandeKind => {
  if (verificationType === "host") return "vehicles";
  if (documentTypes.some((type) => VEHICLE_DOCUMENT_TYPES.has(type))) return "vehicles";
  return "documents";
};

export const buildDocumentDemandeLink = (documentId: string | null): string => {
  const params = new URLSearchParams({
    type: "documents",
    status: "pending",
  });
  if (documentId) {
    params.set("id", documentId);
  }
  return `/admin/documents?${params.toString()}`;
};

export interface PendingDocumentRecord {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  type: string;
  fileUrl: string;
  status: string;
  verificationType: string | null;
  verificationRequestId: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export interface PendingDocumentGroup {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  verificationType: "tenant" | "host";
  documents: PendingDocumentRecord[];
  createdAt: string;
  demandeKind: DemandeKind;
}

const inferVerificationType = (document: PendingDocumentRecord): "tenant" | "host" => {
  if (document.verificationType === "host" || VEHICLE_DOCUMENT_TYPES.has(document.type)) {
    return "host";
  }
  return "tenant";
};

const groupKeyForDocument = (document: PendingDocumentRecord): string => {
  const verificationType = inferVerificationType(document);
  if (document.verificationRequestId) {
    return `pending-docs-${document.verificationRequestId}`;
  }
  return `pending-user-${document.userId}-${verificationType}`;
};

/**
 * Documents already rendered on a request stay there.
 * Remaining pending documents become their own queue cards so "En attente"
 * matches the notification feed.
 */
export const groupPendingDocumentsMissingFromRequests = (
  existingDocumentIds: ReadonlySet<string>,
  pendingDocuments: PendingDocumentRecord[]
): PendingDocumentGroup[] => {
  const groups = new Map<string, PendingDocumentRecord[]>();

  for (const document of pendingDocuments) {
    if (!document.userId || !isPendingReviewStatus(document.status)) continue;
    if (existingDocumentIds.has(document.id)) continue;

    const key = groupKeyForDocument(document);
    const current = groups.get(key) ?? [];
    current.push(document);
    groups.set(key, current);
  }

  return Array.from(groups.entries()).map(([id, documents]) => {
    const first = documents[0];
    const verificationType = inferVerificationType(first);
    const createdAt = documents
      .map((document) => document.createdAt)
      .sort()[0];

    return {
      id,
      userId: first.userId,
      userName: first.userName,
      userEmail: first.userEmail,
      verificationType,
      documents,
      createdAt,
      demandeKind: classifyDemandeKind(
        verificationType,
        documents.map((document) => document.type)
      ),
    };
  });
};
